import React, { useRef, useEffect, useState } from "react";
import { Container, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import topicsData from "../data/topics";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Home.css";

gsap.registerPlugin(ScrollTrigger);

// ─── 定数 ─────────────────────────────────────────────────────────────────
const STORAGE_KEY = "topics_last_seen";
const MONTHS_RANGE = 3;

// ─── 3ヶ月以内の記事を取得 ────────────────────────────────────────────────
function getRecentTopics() {
  const now = new Date();
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - MONTHS_RANGE);
  return topicsData.filter((t) => new Date(t.date) >= cutoff);
}

// ─── タイプライターフック ─────────────────────────────────────────────────
function useTypewriter(text, speed = 80, startDelay = 800) {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => {
    setDisplayed(""); setDone(false);
    let i = 0, iv;
    const t = setTimeout(() => {
      iv = setInterval(() => {
        i++;
        setDisplayed(text.slice(0, i));
        if (i >= text.length) { clearInterval(iv); setDone(true); }
      }, speed);
    }, startDelay);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, [text, speed, startDelay]);
  return { displayed, done };
}

// ─── 破裂エフェクト ──────────────────────────────────────────────
function spawnBurst(ctx, x, y, r) {
  const count = Math.floor(r * 3 + 6);
  const pieces = [];
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 / count) * i + (Math.random() - 0.5) * 0.4;
    const speed = Math.random() * r * 0.8 + r * 0.3;
    pieces.push({ x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
      r: Math.random() * 2.5 + 0.5, alpha: 0.9 });
  }
  const ring = { x, y, r: r * 0.5, maxR: r * 3.5, alpha: 0.7 };
  const draw = () => {
    let alive = false;
    if (ring.alpha > 0) {
      ctx.beginPath();
      ctx.arc(ring.x, ring.y, ring.r, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(180,220,255,${ring.alpha})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ring.r += (ring.maxR - ring.r) * 0.18;
      ring.alpha -= 0.045;
      alive = true;
    }
    pieces.forEach(p => {
      if (p.alpha <= 0) return;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(200,230,255,${p.alpha})`;
      ctx.fill();
      p.x += p.vx; p.y += p.vy;
      p.vx *= 0.92; p.vy *= 0.92; p.vy += 0.09;
      p.alpha -= 0.028;
      alive = true;
    });
    if (alive) requestAnimationFrame(draw);
  };
  draw();
}

// ─── 泡パーティクルCanvas ─────────────────────────────────────────────────
function BubbleCanvas({ className = "", interactive = false }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const particles = [];

    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize, { passive: true });

    class Particle {
      constructor(forceY) { this.reset(forceY); }
      reset(forceY) {
        this.x = Math.random() * canvas.width;
        this.y = forceY !== undefined ? forceY : canvas.height + Math.random() * 80;
        this.r = Math.random() * 8 + 3;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = -(Math.random() * 0.7 + 0.25);
        this.alpha = Math.random() * 0.32 + 0.07;
        this.wobble = Math.random() * Math.PI * 2;
        this.wobbleSpd = Math.random() * 0.025 + 0.005;
        this.popped = false;
      }
      update() {
        if (this.popped) return;
        this.wobble += this.wobbleSpd;
        this.x += this.vx + Math.sin(this.wobble) * 0.35;
        this.y += this.vy;
        this.alpha -= 0.00035;
        if (this.y < -20 || this.alpha <= 0) this.reset();
      }
      draw() {
        if (this.popped) return;
        ctx.save();
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
        const g = ctx.createRadialGradient(
          this.x - this.r * 0.35, this.y - this.r * 0.35, this.r * 0.08,
          this.x, this.y, this.r
        );
        g.addColorStop(0,   `rgba(255,255,255,${this.alpha * 2.8})`);
        g.addColorStop(0.5, `rgba(210,230,255,${this.alpha * 0.5})`);
        g.addColorStop(1,   `rgba(180,210,255,${this.alpha})`);
        ctx.fillStyle = g;
        ctx.fill();
        ctx.strokeStyle = `rgba(255,255,255,${this.alpha * 2})`;
        ctx.lineWidth = 0.6;
        ctx.stroke();
        ctx.restore();
      }
    }

    for (let i = 0; i < 90; i++) particles.push(new Particle(Math.random() * canvas.height));

    const loop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => { p.update(); p.draw(); });
      animId = requestAnimationFrame(loop);
    };
    loop();

    const tryPop = (cx, cy) => {
      const rect = canvas.getBoundingClientRect();
      const mx = cx - rect.left, my = cy - rect.top;
      particles.forEach(p => {
        if (p.popped) return;
        const dx = p.x - mx, dy = p.y - my;
        if (Math.sqrt(dx * dx + dy * dy) < p.r + 10) {
          p.popped = true;
          spawnBurst(ctx, p.x, p.y, p.r);
          setTimeout(() => p.reset(), 700);
        }
      });
    };

    if (interactive) {
      const onClick = e => tryPop(e.clientX, e.clientY);
      const onPointerMove = e => {
        if (e.isPrimary === false) return;
        tryPop(e.clientX, e.clientY);
      };
      document.addEventListener("click", onClick);
      document.addEventListener("pointermove", onPointerMove, { passive: true });
      return () => {
        cancelAnimationFrame(animId);
        window.removeEventListener("resize", resize);
        document.removeEventListener("click", onClick);
        document.removeEventListener("pointermove", onPointerMove);
      };
    }
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, [interactive]);

  return (
    <canvas ref={canvasRef} className={`bubble-canvas ${className}`} aria-hidden="true"
      style={{ pointerEvents: "none" }} />
  );
}

// ─── パララックス画像ブロック ─────────────────────────────────────
function ParallaxBlock({ src, alt, fromLeft, triggerRef }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !triggerRef?.current) return;
    gsap.fromTo(ref.current,
      { opacity: 0, x: fromLeft ? -80 : 80 },
      { opacity: 1, x: 0, duration: 1.2, ease: "power3.out",
        scrollTrigger: { trigger: triggerRef.current, start: "top 75%" } }
    );
  }, [fromLeft, triggerRef]);
  return (
    <div ref={ref} className={`parallax-block ${fromLeft ? "from-left" : "from-right"}`}>
      <img src={src} alt={alt} />
    </div>
  );
}

// ─── トピックス更新ダイアログ ─────────────────────────────────────────────
function TopicsNotification({ topics, onClose }) {
  const navigate = useNavigate();
  const dialogRef = useRef(null);

  // マウント時にフェードイン
  useEffect(() => {
    if (dialogRef.current) {
      gsap.fromTo(dialogRef.current,
        { opacity: 0, y: 24, scale: 0.97 },
        { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "power3.out", delay: 1.2 }
      );
    }
  }, []);

  const handleClose = () => {
    if (dialogRef.current) {
      gsap.to(dialogRef.current, {
        opacity: 0, y: 16, scale: 0.97, duration: 0.3, ease: "power2.in",
        onComplete: onClose,
      });
    } else {
      onClose();
    }
  };

  const handleItemClick = (topicId) => {
    handleClose();
    setTimeout(() => navigate(`/topics/${topicId}#page-top`), 320);
  };

  const handleListClick = () => {
    handleClose();
    setTimeout(() => navigate("/topics#page-top"), 320);
  };

  return (
    <div ref={dialogRef} className="tn-dialog" role="dialog" aria-label="最新トピックス" style={{ opacity: 0 }}>
      {/* ヘッダー */}
      <div className="tn-header">
        <div className="tn-header-left">
          <span className="tn-dot" aria-hidden="true" />
          <span className="tn-title">最新トピックス</span>
          <span className="tn-badge">{topics.length}件</span>
        </div>
        <button className="tn-close" onClick={handleClose} aria-label="閉じる">✕</button>
      </div>

      {/* リスト */}
      <ul className="tn-list">
        {topics.map((topic) => {
          const dateStr = new Date(topic.date).toLocaleDateString("ja-JP", {
            year: "numeric", month: "long", day: "numeric",
          });
          return (
            <li key={topic.id} className="tn-item">
              <button className="tn-item-btn" onClick={() => handleItemClick(topic.id)}>
                <span className="tn-item-dot" aria-hidden="true" />
                <span className="tn-item-body">
                  <span className="tn-item-date">{dateStr}</span>
                  <span className="tn-item-title">{topic.title}</span>
                  <span className="tn-item-summary">{topic.summary}</span>
                </span>
                <span className="tn-item-arrow" aria-hidden="true">›</span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* フッター */}
      <div className="tn-footer">
        <button className="tn-footer-link" onClick={handleListClick}>
          トピックス一覧を見る →
        </button>
      </div>
    </div>
  );
}

// ─── メインコンポーネント ────────────────────────────────────────
export default function Home() {
  const scrollHintRef     = useRef(null);
  const messageSectionRef = useRef(null);
  const featuresRef       = useRef(null);
  const ctaSectionRef     = useRef(null);

  // ─── トピックス通知の表示制御 ─────────────────────────────────
  // 「当日〜3ヶ月前」の記事があり、かつ未確認のものがある場合にダイアログ表示
  const [notifTopics, setNotifTopics] = useState([]);
  const [showNotif, setShowNotif] = useState(false);

  useEffect(() => {
    const recent = getRecentTopics();
    if (recent.length === 0) return;

    // 最新記事の日付をlocalStorageと比較
    const newestDate = recent[0].date; // topicsDataはすでに日付降順
    const lastSeen = localStorage.getItem(STORAGE_KEY);

    // 一度も見ていない、または前回確認より新しい記事がある場合に表示
    if (!lastSeen || lastSeen < newestDate) {
      setNotifTopics(recent);
      setShowNotif(true);
    }
  }, []);

  const handleNotifClose = () => {
    // 閉じた時点の最新記事日付を保存
    if (notifTopics.length > 0) {
      localStorage.setItem(STORAGE_KEY, notifTopics[0].date);
    }
    setShowNotif(false);
  };

  // ─── タイプライター ───────────────────────────────────────────
  const { displayed: homeheroText, done: homeheroDone } = useTypewriter("未来を築く", 110, 700);
  const subDelay = homeheroDone ? 200 : 999999;
  const { displayed: subText } = useTypewriter("ウェーハ洗浄技術の革新へ", 55, subDelay);
  const subFull = "ウェーハ洗浄技術の革新へ";

  useEffect(() => {
    if (subText.length === subFull.length && scrollHintRef.current) {
      gsap.to(scrollHintRef.current, { opacity: 1, duration: 1.2, delay: 0.8 });
    }
  }, [subText]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(messageSectionRef.current,
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 1, ease: "power3.out",
          scrollTrigger: { trigger: messageSectionRef.current, start: "top 80%" } }
      );
      gsap.fromTo(".feature-card",
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", stagger: 0.15,
          scrollTrigger: { trigger: featuresRef.current, start: "top 80%" } }
      );
      gsap.fromTo(ctaSectionRef.current,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 1, ease: "power3.out",
          scrollTrigger: { trigger: ctaSectionRef.current, start: "top 80%" } }
      );
    });
    return () => ctx.revert();
  }, []);

  return (
    <div className="home-page">
      <div id="page-top" />

      {/* ── トピックス通知ダイアログ ──────────────────────────── */}
      {showNotif && (
        <TopicsNotification topics={notifTopics} onClose={handleNotifClose} />
      )}

      {/* ── Hero ──────────────────────────────────────────── */}
      <div className="home-hero-wrapper">
        <BubbleCanvas interactive={true} />

        <video className="home-hero-video" autoPlay muted loop playsInline preload="auto"
          disablePictureInPicture controlsList="nodownload nofullscreen noremoteplayback">
          <source src="/videos/company.mp4" type="video/mp4" />
        </video>
        <div className="home-hero-video-overlay" aria-hidden="true" />

        <div className="home-hero-overlay">
          <div className="home-hero-text-block">
            <div className="home-hero-label">ZENKYO CORPORATION</div>

            <h1 className="home-hero-title">
              {homeheroText}
              <span className={`tw-cursor ${homeheroDone ? "tw-cursor--blink" : ""}`}>|</span>
            </h1>

            <p className="home-hero-sub">
              {subText}
              {subText.length > 0 && subText.length < subFull.length && (
                <span className="tw-cursor tw-cursor--blink">|</span>
              )}
            </p>

            <div className="home-hero-divider" />

            <div className="home-hero-cta-row">
              <Button as={Link} to="/about#page-top" className="home-hero-btn home-hero-btn--primary">
                会社概要
              </Button>
              <Button as={Link} to="/products#page-top" className="home-hero-btn home-hero-btn--ghost">
                製品情報
              </Button>
            </div>
          </div>

          <div className="home-scroll-down" ref={scrollHintRef} style={{ opacity: 0 }}>
            <span>Scroll</span>
            <div className="arrow" />
          </div>
        </div>
      </div>

      {/* ── メッセージ ─────────────────────────────────────── */}
      <section className="home-message-section" ref={messageSectionRef}>
        <Container>
          <div className="message-grid">
            <div className="message-text">
              <div className="section-label">OUR MISSION</div>
              <h2>洗浄技術の<br />普遍と創造<br />そして革新へ</h2>
              <p>
                半導体産業の最前線で、<br />
                私たちは洗浄技術の限界を<br />
                常に押し広げ続けています。
              </p>
              <Button as={Link} to="/about#page-top" className="msg-btn">
                詳しく見る →
              </Button>
            </div>
            <div className="message-visual">
              <ParallaxBlock src="/images/product.png" alt="製品画像"
                fromLeft={false} triggerRef={messageSectionRef} />
            </div>
          </div>
        </Container>
      </section>

      {/* ── フィーチャー ───────────────────────────────────── */}
      <section className="home-features-section" ref={featuresRef}>
        <Container>
          <div className="section-label text-center">STRENGTHS</div>
          <div className="features-grid">
            {[
              { icon: "⚙️", title: "高精度ウェーハ洗浄技術",    desc: "最先端の洗浄プロセス技術により、半導体ウェーハ表面の微細なパーティクルや汚染物質を効果的に除去。高度化する半導体製造プロセスに対応し、安定した品質と高い再現性を実現する洗浄装置を提供しています。最先端の技術で半導体ウェーハの洗浄精度を極限まで追求します。" },
              { icon: "🔬", title: "先端プロセスを支える研究開発力",        desc: "長年培ってきた洗浄技術の知見と独自の研究開発体制により、半導体製造の高度化に対応する新しいプロセス技術を追求。次世代デバイスの製造を支える洗浄ソリューションの開発に取り組んでいます。" },
              { icon: "🌐", title: "グローバル市場での導入実績", desc: "国内外の半導体メーカーへの装置納入実績を背景に、世界各国の製造ラインで当社の技術が活用されています。多様化する製造ニーズに対応し、グローバルな半導体産業の発展に貢献しています。" },
            ].map(f => (
              <div key={f.title} className="feature-card">
                <div className="feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── CTA ───────────────────────────────────────────── */}
      <section className="home-cta-section" ref={ctaSectionRef}>
        <BubbleCanvas interactive={true} />
        <div className="cta-inner">
          <div className="section-label">GET IN TOUCH</div>
          <h2>ビジネスも、キャリアも、次のステージへ。</h2>
          <div className="cta-btn-row">
            <Button as={Link} to="/contact#page-top" className="hero-btn hero-btn--primary">
              お問い合わせ
            </Button>
            <Button as={Link} to="/recruit#page-top" className="hero-btn hero-btn--ghost">
              採用情報
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}